import { IImportable } from 'library/ts/interfaces/importable/IImportable';
import { IImporterReject } from 'library/ts/interfaces/importer/IImporterReject';
import { IImporterResolve } from 'library/ts/interfaces/importer/IImporterResolve';
import { IImportVO } from 'library/ts/interfaces/importer/IImportVO';
import { ImportVO } from 'library/ts/model/vo/ImportVO';
import { ILogger } from 'library/ts/utilities/logger/interfaces/ILogger';
import { LogService } from 'library/ts/utilities/logger/LogService';
import { Utilities } from 'library/ts/utilities/Utilities';
import { ISmartTag } from 'tag/ts/smart_tag/interfaces/ISmartTag';
import { ISmartTagReadyCallback } from 'tag/ts/smart_tag/interfaces/ISmartTagReadyCallback';
import { Constants } from 'tag/ts/smart_tag/model/Constants';
import { TSmartTagReadyCallbackMethod } from 'tag/ts/smart_tag/types/TSmartTagReadyCallbackMethod';
import { IImports } from 'tag/ts/tag/interfaces/config/imports/IImports';
import { NewRelicManager } from 'tag/ts/tag/metrics/NewRelicManager';

const logger: ILogger = LogService.create( { prefixes: [ 'SmartTag:' ] } );

/**
 * @description Preloads imports
 * @param {IImports} importsConfig
 * @return {Promise<IImporterResolve | IImporterReject>}
 */
const preloadImports = ( importsConfig: IImports ): Promise<IImporterResolve | IImporterReject> => {
	const utils: Utilities = window.SmartTag.utilities;
	const runtimeVO = window.SmartTag.runtimeVO;

	// if relative loading model is requested
	if ( runtimeVO?.readyCallback?.payload?.relative === true ) {
		// loop over imports config object + convert matching URLs to relative to smart-tag import
		Object.keys( importsConfig ).forEach(
			( key ) => {
				const oneImport: IImportable = ( importsConfig )[ key ] as IImportable;
				oneImport.url.regular = utils.URL.convertToRelative( oneImport?.url?.regular );
			}
		);
	}

	const imports: IImportVO[] = []; // create placeholder array for importVO objects

	Object.keys( importsConfig ).forEach(
		( key ) => {
			const importable: IImportable = importsConfig[ key ] as IImportable; // one importable
			const importType: string = 'regular'; // regular / minified
			// if import is not present => add import to list
			if ( utils.Import.alreadyPresent( importable.url[ importType ], importable.namespace ) === false ) {
				let url: string = importable.url[ importType ];
				if ( url.indexOf( window.PlayerServicesProjectInfo.app_output_file.tag.js.min.value.replace( '.js', '' ) ) > -1 ) {
					url = utils.URL.addCacheToken( url, runtimeVO.debug.cacheBust );
				}

				const newImport = new ImportVO(
					importable.type,
					url,
					importable.namespace,
					window
				);
				newImport.order = importable.order;

				imports.push( newImport );
				imports.sort( utils.Array.sortByProperty( 'order' ) ); // add importVO to imports array
			}
		}
	);

	logger.log( 'preloadImports:', 'imports', imports );

	// Return it as a Promise
	return new Promise(
		( resolve, reject ) => {
			utils.Importer.import( imports, true ).then(
				( data: IImporterResolve ) => {
					logger.log( 'preloadImports:', 'Importer:', 'Done:', data );
					resolve( data );
				}
			).catch(
				( error: IImporterReject ) => {
					logger.warn( 'preloadImports:', 'Importer:', 'ERROR:', error );
					reject( error );
				}
			);
		}
	);
};

/**
 * @description Informs page that SmartTag is ready... if
 */
export const executeSmartTagReadyPageCallback = ( smartTag: ISmartTag ): void => {
	const utils: Utilities = smartTag.utilities;
	const callbackObject: ISmartTagReadyCallback = smartTag?.runtimeVO?.readyCallback;

	const executeReadyCallback = (): void => {
		if ( utils.Type.isFunction( callbackObject.method ) ) {
			logger.log(
				'executeReadyCallback:',
				'Default callback was found:',
				callbackObject.method,
				'Execute...'
			);

			try {
				utils.Delay.delay(
					utils.Type.isNumber( callbackObject.delay ) ? callbackObject.delay : undefined
				)
					.then(
						() => {
							( callbackObject.method as TSmartTagReadyCallbackMethod )( smartTag ); // execute default onReady callback
						}
					);
			} catch ( error ) {
				logger.warn(
					'executeReadyCallback:',
					'Callback error:',
					error
				);
			}
		} else {
			logger.warn(
				'executeReadyCallback:',
				'Callback is not set or',
				'Default callback:',
				Constants.getInstance().FUNCTION_SMART_TAG_ON_READY,
				'does not exist on page.',
				'Abort.'
			);
		}
	};

	if ( utils.Type.isObject( callbackObject ) ) {
		utils.Delay.delay().then(
			() => {
				// initialize New Relic
				NewRelicManager.getInstance().initialize( smartTag?.runtimeVO, smartTag?.info?.version )
					.then(
						( data ) => {
							logger.log( 'executeSmartTagReadyPageCallback:', 'New Relic initialized:', data, 'Execute callback.' );
						}
					)
					.then( () => utils.Delay.delay( 10 ) )
					.then(
						() => {
							// do we have imports to preload?
							if ( utils.Type.isObject( callbackObject?.payload?.imports ) && Object.keys( callbackObject.payload.imports ).length > 0 ) {
								logger.log( 'Preload imports...' );
								preloadImports( callbackObject.payload.imports ).then(
									( data: IImporterResolve ) => {
										logger.log( 'preloadImports:', 'Preload success:', data, 'Execute callback.' );
										executeReadyCallback();
									}
								).catch(
									( error: IImporterReject ) => {
										logger.warn( 'preloadImports:', 'Preload failure:', error, 'Execute callback.' );
										executeReadyCallback();
									}
								);
							} else {
								executeReadyCallback();
							}
						}
					)
					.catch(
						( data ) => {
							logger.warn( 'executeSmartTagReadyPageCallback:', 'ERROR:', data );
						}
					);
			}
		);
	}
};